import { useUpdateUser } from '../../hooks/admin/useAdminMutations'
import { useAdminUser } from '../../hooks/admin/useAdminQueries'
import { useAdminStore } from '../../stores/adminStore'
import { AdminUpdateUserDto, TargetAudienceEntryDto } from '../../types/admin-types'
import React, { useState, useEffect } from 'react'

// Assuming UserTimeZones enum exists and is imported, otherwise use string
// import { UserTimeZones } from '../../../common/enums/user-timezones.enum'; // Adjust path
const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    // Add other common timezones as needed
]

const EditUserModal: React.FC = () => {
    const isOpen = useAdminStore((state) => state.isEditUserModalOpen)
    const editingUserId = useAdminStore((state) => state.editingUserId)
    const closeModal = useAdminStore((state) => state.closeEditUserModal)

    // Fetch current user data
    const {
        data: currentUserData,
        isLoading: isLoadingUser,
        isError: isUserError,
        error: userError,
    } = useAdminUser(editingUserId)

    // Update mutation
    const {
        mutate: updateUser,
        isPending: isUpdating,
        isError: isUpdateError,
        error: updateError,
        isSuccess: isUpdateSuccess,
        reset: resetMutation,
    } = useUpdateUser()

    // Form state initialization requires careful handling due to async user load
    const [formData, setFormData] = useState<Partial<AdminUpdateUserDto>>({})
    const [targetAudience, setTargetAudience] = useState<TargetAudienceEntryDto[]>([])

    // Effect to populate form when user data loads or editingUserId changes
    useEffect(() => {
        if (currentUserData) {
            // Populate basic fields
            const basicData: Partial<AdminUpdateUserDto> = {
                firstName: currentUserData.first_name,
                lastName: currentUserData.last_name,
                title: currentUserData.title,
                email: currentUserData.email,
                userOrgName: currentUserData.user_org_name,
                alternate_names: currentUserData.alternate_names,
                default_timezone: currentUserData.default_timezone,
                // relevantTopics is handled separately below
            }
            setFormData(basicData)

            // Populate relevant topics
            setFormData((prev) => ({
                ...prev,
                relevantTopics: {
                    bio: currentUserData.userRelevantTopics?.bio ?? '',
                    tone_of_voice: currentUserData.userRelevantTopics?.tone_of_voice ?? '',
                    // target_audience_structured handled by separate state
                },
            }))
            setTargetAudience(currentUserData.userRelevantTopics?.target_audience_structured ?? [])
        } else {
            // Reset form if no user data (e.g., modal closed, ID cleared)
            setFormData({})
            setTargetAudience([])
        }
    }, [currentUserData, editingUserId])

    // Effect to reset mutation state and close modal on success or closure
    useEffect(() => {
        if (!isOpen || isUpdateSuccess) {
            resetMutation()
        }
        if (isUpdateSuccess) {
            closeModal()
            // Add success toast?
        }
    }, [isOpen, isUpdateSuccess, resetMutation, closeModal])

    const handleBasicChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    const handleRelevantTopicsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target // name will be 'bio' or 'tone_of_voice'
        setFormData((prev) => ({
            ...prev,
            relevantTopics: {
                ...(prev.relevantTopics || {}),
                [name]: value,
            },
        }))
    }

    const handleAudienceChange = (
        index: number,
        field: keyof TargetAudienceEntryDto,
        value: string,
    ) => {
        const updatedAudience = [...targetAudience]
        updatedAudience[index] = { ...updatedAudience[index], [field]: value }
        setTargetAudience(updatedAudience)
    }

    const addAudienceEntry = () => {
        setTargetAudience([...targetAudience, { audience_name: '', description: '' }])
    }

    const removeAudienceEntry = (index: number) => {
        setTargetAudience(targetAudience.filter((_, i) => i !== index))
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!editingUserId) return

        // Combine form data with target audience for the final update DTO
        const updateData: AdminUpdateUserDto = {
            ...formData,
            relevantTopics: {
                ...(formData.relevantTopics || {}),
                target_audience_structured: targetAudience,
            },
        }

        updateUser({ id: editingUserId, data: updateData })
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h2 className="text-xl font-semibold">Edit User</h2>
                    <button
                        onClick={closeModal}
                        className="text-gray-400 hover:text-gray-600"
                        disabled={isUpdating}
                    >
                        {/* Close Icon */}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {isLoadingUser ? (
                    <div className="text-center p-8">Loading user data...</div>
                ) : isUserError ? (
                    <div className="text-center p-8 text-red-600">
                        Error loading user data: {userError?.message || 'Unknown error'}
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Information Section */}
                        <fieldset className="border p-4 rounded-md">
                            <legend className="text-lg font-medium px-2">Basic Information</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                <div>
                                    <label
                                        htmlFor="edit-firstName"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        id="edit-firstName"
                                        name="firstName"
                                        value={formData.firstName || ''}
                                        onChange={handleBasicChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="edit-lastName"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        id="edit-lastName"
                                        name="lastName"
                                        value={formData.lastName || ''}
                                        onChange={handleBasicChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="edit-title"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Title
                                    </label>
                                    <input
                                        type="text"
                                        id="edit-title"
                                        name="title"
                                        value={formData.title || ''}
                                        onChange={handleBasicChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="edit-email"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="edit-email"
                                        name="email"
                                        value={formData.email || ''}
                                        onChange={handleBasicChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="edit-userOrgName"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        User's Company Name
                                    </label>
                                    <input
                                        type="text"
                                        id="edit-userOrgName"
                                        name="userOrgName"
                                        value={formData.userOrgName || ''}
                                        onChange={handleBasicChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="edit-alternate_names"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Alternate Names (comma-sep)
                                    </label>
                                    <input
                                        type="text"
                                        id="edit-alternate_names"
                                        name="alternate_names"
                                        value={formData.alternate_names || ''}
                                        onChange={handleBasicChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="edit-default_timezone"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Default Timezone
                                    </label>
                                    <select
                                        id="edit-default_timezone"
                                        name="default_timezone"
                                        value={formData.default_timezone || 'UTC'}
                                        onChange={handleBasicChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    >
                                        {timezones.map((tz) => (
                                            <option key={tz} value={tz}>
                                                {tz}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </fieldset>

                        {/* Relevant Topics Section */}
                        <fieldset className="border p-4 rounded-md">
                            <legend className="text-lg font-medium px-2">Relevant Topics</legend>
                            <div className="space-y-4 mt-2">
                                <div>
                                    <label
                                        htmlFor="edit-bio"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Bio
                                    </label>
                                    <textarea
                                        id="edit-bio"
                                        name="bio"
                                        rows={4}
                                        value={formData.relevantTopics?.bio || ''}
                                        onChange={handleRelevantTopicsChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="edit-tone_of_voice"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Tone of Voice
                                    </label>
                                    <textarea
                                        id="edit-tone_of_voice"
                                        name="tone_of_voice"
                                        rows={3}
                                        value={formData.relevantTopics?.tone_of_voice || ''}
                                        onChange={handleRelevantTopicsChange}
                                        className="mt-1 block w-full input-field"
                                        disabled={isUpdating}
                                    />
                                </div>

                                {/* Target Audience Editor */}
                                <div>
                                    <h4 className="text-md font-medium text-gray-800 mb-2">
                                        Target Audience (Structured)
                                    </h4>
                                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                                        {targetAudience.map((audience, index) => (
                                            <div
                                                key={index}
                                                className="flex items-start space-x-2 p-2 border rounded bg-gray-50"
                                            >
                                                <div className="flex-grow grid grid-cols-1 gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder="Audience Name (e.g., CEOs)"
                                                        value={audience.audience_name}
                                                        onChange={(e) =>
                                                            handleAudienceChange(
                                                                index,
                                                                'audience_name',
                                                                e.target.value,
                                                            )
                                                        }
                                                        className="input-field text-sm"
                                                        disabled={isUpdating}
                                                    />
                                                    <textarea
                                                        placeholder="Description"
                                                        rows={2}
                                                        value={audience.description}
                                                        onChange={(e) =>
                                                            handleAudienceChange(
                                                                index,
                                                                'description',
                                                                e.target.value,
                                                            )
                                                        }
                                                        className="input-field text-sm resize-none"
                                                        disabled={isUpdating}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeAudienceEntry(index)}
                                                    className="mt-1 text-red-500 hover:text-red-700 p-1 disabled:opacity-50"
                                                    title="Remove Audience"
                                                    disabled={isUpdating}
                                                >
                                                    {/* Trash Icon */}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-4 w-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addAudienceEntry}
                                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                                        disabled={isUpdating}
                                    >
                                        + Add Audience Entry
                                    </button>
                                </div>
                            </div>
                        </fieldset>

                        {/* Error Message */}
                        {isUpdateError && (
                            <div className="text-red-600 text-sm mt-4 text-center">
                                Error updating user:{' '}
                                {updateError instanceof Error
                                    ? updateError.message
                                    : 'Unknown error'}
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="pt-4 border-t flex justify-end space-x-3 mt-6">
                            <button
                                type="button"
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
                                disabled={isUpdating}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isUpdating}
                            >
                                {isUpdating ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
            {/* Simple global CSS for input fields within the modal */}
            <style>{`
        .input-field {
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db; /* gray-300 */
          border-radius: 0.375rem; /* rounded-md */
          box-shadow: inset 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }
        .input-field:focus {
          outline: 2px solid transparent;
          outline-offset: 2px;
          border-color: #6366f1; /* indigo-500 */
          box-shadow: 0 0 0 2px #a5b4fc; /* ring-indigo-300 */
        }
        .input-field:disabled {
            background-color: #f3f4f6; /* gray-100 */
            opacity: 0.7;
            cursor: not-allowed;
        }
        /* Adjust textarea height */
        textarea.input-field {
            min-height: 60px;
        }
      `}</style>
        </div>
    )
}

export default EditUserModal
